import { FormErrors, useForm, UseFormReturnType } from '@mantine/form';
import { z } from 'zod';
import { getValidateErrors } from '@/lib/utils/validation';
import {
  DlpFileTypesMode,
  DlpOnTimeout,
  DlpProfile,
  IcapServerDto,
  SaveDlpProfileRequest
} from '@/services/api/dlpSettingsApiArea/types';
import { dlpSettingsApiArea } from '@/services/api/dlpSettingsApiArea';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

const createSchema = (t: (key: string) => string) =>
  z
    .object({
      name: z.string().min(1, t('dlp.validation.nameRequired')),
      type: z.string().default(''),
      host: z
        .string()
        .min(1, t('dlp.validation.hostRequired'))
        .refine(
          (val) => {
            // Проверка IP адреса (IPv4)
            const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
            // Проверка доменного имени
            const domainRegex =
              /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
            // Проверка localhost
            if (val === 'localhost') return true;
            // Проверка IPv4
            if (ipv4Regex.test(val)) {
              const parts = val.split('.');
              return parts.every((part) => parseInt(part) >= 0 && parseInt(part) <= 255);
            }
            // Проверка домена
            return domainRegex.test(val);
          },
          {
            message: t('dlp.validation.hostInvalid')
          }
        ),
      port: z.coerce
        .number()
        .min(1, t('dlp.validation.portRequired'))
        .max(65535, t('dlp.validation.portInvalid')),
      reqmodService: z.string().refine((val) => val === '' || val.startsWith('/'), {
        message: t('dlp.validation.pathMustStartWithSlash')
      }),
      respmodService: z.string().refine((val) => val === '' || val.startsWith('/'), {
        message: t('dlp.validation.pathMustStartWithSlash')
      }),
      connectTimeoutMs: z.coerce.number().min(0).max(300000, t('dlp.validation.max5Minutes')),
      writeTimeoutMs: z.coerce.number().min(0).max(300000, t('dlp.validation.max5Minutes')),
      readTimeoutMs: z.coerce.number().min(0).max(300000, t('dlp.validation.max5Minutes')),
      resultTimeoutMs: z.coerce.number().min(0).max(600000, t('dlp.validation.max10Minutes')),
      attempts: z.coerce.number().min(0).max(100, t('dlp.validation.max100Attempts')),
      backoffMs: z.coerce.number().min(0).max(60000, t('dlp.validation.max1Minute')),
      maxConnections: z.coerce
        .number()
        .nullable()
        .refine((val) => val === null || (val > 0 && val <= 10000), {
          message: t('dlp.validation.range1To10000')
        }),
      onTimeout: z.nativeEnum(DlpOnTimeout),
      scanUpload: z.boolean(),
      scanDownload: z.boolean(),
      scanShareInternal: z.boolean(),
      scanShareGuest: z.boolean(),
      scanExternalLink: z.boolean(),
      fileTypesMode: z.nativeEnum(DlpFileTypesMode),
      allowedExt: z.string(),
      blockedExt: z.string(),
      allowedMime: z.string(),
      blockedMime: z.string(),
      maxFileSizeMb: z.coerce
        .number()
        .nullable()
        .refine((val) => val === null || (val >= 0 && val <= 10240), {
          message: t('dlp.validation.maxFileSizeRange')
        }),
      whenDownWaitMs: z.coerce.number().min(0).max(3600000, t('dlp.validation.max1Hour')),
      maxMessageSizeKb: z.coerce
        .number()
        .nullable()
        .refine((val) => val === null || val >= 0, {
          message: t('dlp.validation.messageSizeNonNegative')
        }),
      skipOnIcapErrors: z.boolean(),
      hashTtlMinutes: z.coerce.number().nullable(),
      useHashCache: z.boolean(),
      headerXScanOp: z.string().default(''),
      headerFileName: z.string().default(''),
      headerFileSize: z.string().default(''),
      headerFileId: z.string().default(''),
      headerFileHash: z.string().default(''),
      headerRecipientEmail: z.string().default(''),
      headerUserEmail: z.string().default(''),
      headerCustomerId: z.string().default(''),
      headerRoleId: z.string().default(''),
      headerRoleName: z.string().default(''),
      headerRoleType: z.string().default('')
    })
    .refine(
      (data) => {
        // Должно быть выбрано хотя бы одно условие сканирования
        return (
          data.scanUpload ||
          data.scanDownload ||
          data.scanShareInternal ||
          data.scanShareGuest ||
          data.scanExternalLink
        );
      },
      {
        message: t('dlp.validation.selectAtLeastOneScanCondition'),
        path: ['scanUpload']
      }
    )
    .refine(
      (data) => {
        // Если режим AllowList, нужен хотя бы один из allowedExt или allowedMime
        if (data.fileTypesMode === DlpFileTypesMode.AllowList) {
          const hasAllowedExt = data.allowedExt.trim().length > 0;
          const hasAllowedMime = data.allowedMime.trim().length > 0;
          return hasAllowedExt || hasAllowedMime;
        }
        return true;
      },
      {
        message: t('dlp.validation.allowListRequiresExtOrMime'),
        path: ['allowedExt']
      }
    )
    .refine(
      (data) => {
        // Если режим DenyList, нужен хотя бы один из blockedExt или blockedMime
        if (data.fileTypesMode === DlpFileTypesMode.DenyList) {
          const hasBlockedExt = data.blockedExt.trim().length > 0;
          const hasBlockedMime = data.blockedMime.trim().length > 0;
          return hasBlockedExt || hasBlockedMime;
        }
        return true;
      },
      {
        message: t('dlp.validation.denyListRequiresExtOrMime'),
        path: ['blockedExt']
      }
    )
    .refine(
      (data) => {
        // Если UseHashCache включен, hashTtlMinutes должен быть > 0
        if (data.useHashCache) {
          return data.hashTtlMinutes !== null && data.hashTtlMinutes > 0;
        }
        return true;
      },
      {
        message: t('dlp.validation.hashCacheRequiresTtl'),
        path: ['hashTtlMinutes']
      }
    )
    .refine(
      (data) => {
        // Если scanUpload включен, reqmodService обязателен
        if (data.scanUpload) {
          return data.reqmodService.trim().length > 0;
        }
        return true;
      },
      {
        message: t('dlp.validation.uploadScanRequiresReqmod'),
        path: ['reqmodService']
      }
    )
    .refine(
      (data) => {
        // Если scanDownload/ShareInternal/ShareGuest/ExternalLink включены, respmodService обязателен
        const needRespmod =
          data.scanDownload ||
          data.scanShareInternal ||
          data.scanShareGuest ||
          data.scanExternalLink;
        if (needRespmod) {
          return data.respmodService.trim().length > 0;
        }
        return true;
      },
      {
        message: t('dlp.validation.downloadScanRequiresRespmod'),
        path: ['respmodService']
      }
    );

export type TFormValuesDlp = z.infer<ReturnType<typeof createSchema>>;

type Return = [
  UseFormReturnType<TFormValuesDlp>,
  () => Promise<DlpProfile | null>,
  IcapServerDto[],
  (server: IcapServerDto) => void,
  (index: number) => void,
  (server: IcapServerDto, index: number) => void,
  boolean
];

const getInitialValues = (profile?: DlpProfile): TFormValuesDlp => {
  if (!profile) {
    return {
      name: '',
      type: '',
      host: '',
      port: 1344,
      reqmodService: '/',
      respmodService: '/',
      connectTimeoutMs: 5000,
      writeTimeoutMs: 15000,
      readTimeoutMs: 15000,
      resultTimeoutMs: 60000,
      attempts: 3,
      backoffMs: 500,
      maxConnections: null,
      onTimeout: DlpOnTimeout.Block,
      scanUpload: true,
      scanDownload: false,
      scanShareInternal: false,
      scanShareGuest: false,
      scanExternalLink: false,
      fileTypesMode: DlpFileTypesMode.Off,
      allowedExt: '',
      blockedExt: '',
      allowedMime: '',
      blockedMime: '',
      maxFileSizeMb: null,
      whenDownWaitMs: 30000,
      maxMessageSizeKb: null,
      skipOnIcapErrors: false,
      hashTtlMinutes: null,
      useHashCache: false,
      headerXScanOp: 'X-Scan-Op',
      headerUserEmail: 'X-User-Email',
      headerRecipientEmail: 'X-Recipient-Email',
      headerFileId: 'X-File-Id',
      headerFileName: 'X-File-Name',
      headerFileSize: 'X-File-Size',
      headerFileHash: 'X-File-Hash',
      headerCustomerId: 'X-Customer-Id',
      headerRoleId: 'X-Role-Id',
      headerRoleName: 'X-Role-Name',
      headerRoleType: 'X-Role-Type'
    };
  }

  return {
    name: profile.Server.Name,
    type: '',
    host: profile.Server.Host,
    port: profile.Server.Port,
    reqmodService: profile.Server.ReqmodService,
    respmodService: profile.Server.RespmodService,
    connectTimeoutMs: profile.ConnectTimeoutMs,
    writeTimeoutMs: profile.WriteTimeoutMs,
    readTimeoutMs: profile.ReadTimeoutMs,
    resultTimeoutMs: profile.ResultTimeoutMs,
    attempts: profile.Attempts,
    backoffMs: profile.BackoffMs,
    maxConnections: profile.MaxConnections,
    onTimeout: profile.OnTimeout,
    scanUpload: (profile.ScanActions & 1) === 1,
    scanDownload: (profile.ScanActions & 2) === 2,
    scanShareInternal: (profile.ScanActions & 4) === 4,
    scanShareGuest: (profile.ScanActions & 8) === 8,
    scanExternalLink: (profile.ScanActions & 16) === 16,
    fileTypesMode: profile.FileTypesMode,
    allowedExt: profile.AllowedExt.join(', '),
    blockedExt: profile.BlockedExt.join(', '),
    allowedMime: profile.AllowedMime.join(', '),
    blockedMime: profile.BlockedMime.join(', '),
    maxFileSizeMb: profile.MaxFileSizeMb,
    whenDownWaitMs: profile.WhenDownWaitMs,
    maxMessageSizeKb: profile.MaxMessageSizeKb,
    skipOnIcapErrors: profile.SkipOnIcapErrors,
    hashTtlMinutes: profile.HashTtlMinutes,
    useHashCache: profile.UseHashCache,
    headerXScanOp: profile.IcapHeaders['Action'] || 'X-Scan-Op',
    headerUserEmail: profile.IcapHeaders['UserEmail'] || 'X-User-Email',
    headerRecipientEmail: profile.IcapHeaders['RecipientEmail'] || 'X-Recipient-Email',
    headerFileId: profile.IcapHeaders['FileId'] || 'X-File-Id',
    headerFileName: profile.IcapHeaders['FileName'] || 'X-File-Name',
    headerFileSize: profile.IcapHeaders['FileSize'] || 'X-File-Size',
    headerFileHash: profile.IcapHeaders['FileHash'] || 'X-File-Hash',
    headerCustomerId: profile.IcapHeaders['CustomerId'] || 'X-Customer-Id',
    headerRoleId: profile.IcapHeaders['RoleId'] || 'X-Role-Id',
    headerRoleName: profile.IcapHeaders['RoleName'] || 'X-Role-Name',
    headerRoleType: profile.IcapHeaders['RoleType'] || 'X-Role-Type'
  };
};

export const useDlpForm = (profile?: DlpProfile): Return => {
  const { t } = useTranslation();
  const schema = createSchema(t);
  const form = useForm<TFormValuesDlp>({
    validateInputOnChange: true,
    initialValues: getInitialValues(profile),
    validate: (values): FormErrors => getValidateErrors(schema, values)
  });

  const initialServers = profile?.Servers || [];
  const [servers, setServers] = useState<IcapServerDto[]>(initialServers);

  // Проверка, изменились ли серверы
  const hasServerChanges = JSON.stringify(servers) !== JSON.stringify(initialServers);

  const addServer = (server: IcapServerDto) => {
    setServers((prev) => [...prev, server]);
  };

  const deleteServer = (index: number) => {
    setServers((prev) => prev.filter((_, i) => i !== index));
  };

  const editServer = (server: IcapServerDto, index: number) => {
    setServers((prev) => prev.map((s, i) => (i === index ? server : s)));
  };

  const handleSubmit = async (): Promise<DlpProfile | null> => {
    const validation = form.validate();
    if (validation.hasErrors) {
      notifications.show({
        title: t('dlp.validationError'),
        message: t('dlp.validationErrorMessage'),
        color: 'red'
      });
      return null;
    }

    try {
      const values = form.values;

      // Валидация дополнительных серверов
      if (servers.length > 0) {
        const needReqmod = values.scanUpload;
        const needRespmod =
          values.scanDownload ||
          values.scanShareInternal ||
          values.scanShareGuest ||
          values.scanExternalLink;

        if (needReqmod || needRespmod) {
          for (const server of servers) {
            const hasReqmod = server.ReqmodService?.trim().length > 0;
            const hasRespmod = server.RespmodService?.trim().length > 0;

            if (!hasReqmod && !hasRespmod) {
              notifications.show({
                title: t('dlp.validationError'),
                message: t('dlp.additionalServersRequireService'),
                color: 'red'
              });
              return null;
            }
          }
        }
      }

      const ScanActions =
        (values.scanUpload ? 1 : 0) |
        (values.scanDownload ? 2 : 0) |
        (values.scanShareInternal ? 4 : 0) |
        (values.scanShareGuest ? 8 : 0) |
        (values.scanExternalLink ? 16 : 0);

      const IcapHeaders: Record<string, string> = {
        Action: values.headerXScanOp || 'X-Scan-Op',
        UserEmail: values.headerUserEmail || 'X-User-Email',
        RecipientEmail: values.headerRecipientEmail || 'X-Recipient-Email',
        FileId: values.headerFileId || 'X-File-Id',
        FileName: values.headerFileName || 'X-File-Name',
        FileSize: values.headerFileSize || 'X-File-Size',
        FileHash: values.headerFileHash || 'X-File-Hash',
        CustomerId: values.headerCustomerId || 'X-Customer-Id',
        RoleId: values.headerRoleId || 'X-Role-Id',
        RoleName: values.headerRoleName || 'X-Role-Name',
        RoleType: values.headerRoleType || 'X-Role-Type'
      };

      const params: SaveDlpProfileRequest = {
        Id: profile?.Id || 0,
        CustomerId: profile?.CustomerId || null,
        IsActive: true,
        Enabled: true,
        Server: {
          Name: values.name,
          Host: values.host,
          Port: values.port,
          ReqmodService: values.reqmodService,
          RespmodService: values.respmodService
        },
        MaxConnections: values.maxConnections,
        ConnectTimeoutMs: values.connectTimeoutMs,
        ReadTimeoutMs: values.readTimeoutMs,
        WriteTimeoutMs: values.writeTimeoutMs,
        Attempts: values.attempts,
        BackoffMs: values.backoffMs,
        ResultTimeoutMs: values.resultTimeoutMs,
        WhenDownWaitMs: values.whenDownWaitMs,
        OnTimeout: values.onTimeout,
        ScanActions,
        FileTypesMode: values.fileTypesMode,
        AllowedExt: values.allowedExt
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean),
        BlockedExt: values.blockedExt
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean),
        AllowedMime: values.allowedMime
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean),
        BlockedMime: values.blockedMime
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean),
        MaxFileSizeMb: values.maxFileSizeMb,
        UseHashCache: values.useHashCache,
        SkipOnIcapErrors: values.skipOnIcapErrors,
        MaxMessageSizeKb: values.maxMessageSizeKb,
        HashTtlMinutes: values.hashTtlMinutes,
        HashAlgo: 'SHA256',
        IcapHeaders,
        Servers: servers
      };

      const savedProfile = await dlpSettingsApiArea().SaveDlpProfile(params);

      notifications.show({
        title: t('dlp.success'),
        message: profile?.Id ? t('dlp.updateSuccess') : t('dlp.createSuccess'),
        color: 'green'
      });

      return savedProfile;
    } catch (error) {
      notifications.show({
        title: t('dlp.saveError'),
        message: error instanceof Error ? error.message : t('dlp.saveErrorMessage'),
        color: 'red'
      });
      return null;
    }
  };

  return [form, handleSubmit, servers, addServer, deleteServer, editServer, hasServerChanges];
};
